/**
 * Конвертирует объект из camelCase в snake_case
 * @param {Object} obj - объект для конвертации
 * @returns {Object} объект с полями в snake_case
 */
export function camelToSnake(obj) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => camelToSnake(item));
  }

  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Конвертируем camelCase в snake_case
      const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      const value = obj[key];
      
      // Рекурсивно обрабатываем вложенные объекты
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        result[snakeKey] = camelToSnake(value);
      } else {
        result[snakeKey] = value;
      }
    }
  }
  return result;
}

/**
 * Конвертирует объект из snake_case в camelCase
 * @param {Object} obj - объект для конвертации
 * @returns {Object} объект с полями в camelCase
 */
export function snakeToCamel(obj) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => snakeToCamel(item));
  }

  const result = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Конвертируем snake_case в camelCase
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      const value = obj[key];
      
      // Рекурсивно обрабатываем вложенные объекты
      if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        result[camelKey] = snakeToCamel(value);
      } else {
        result[camelKey] = value;
      }
    }
  }
  return result;
}

/**
 * Нормализует данные для отправки на сервер
 * Конвертирует camelCase поля в snake_case, если они есть
 * Приоритет отдается snake_case полям, если они уже существуют
 * @param {Object} data - данные для нормализации
 * @param {Array<string>} fieldsToNormalize - список полей для нормализации (если не указан, нормализуются все)
 * @returns {Object} нормализованные данные
 */
export function normalizeForBackend(data, fieldsToNormalize = null) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => normalizeForBackend(item, fieldsToNormalize));
  }

  const result = { ...data };

  // Если указаны конкретные поля, нормализуем только их
  if (fieldsToNormalize && Array.isArray(fieldsToNormalize)) {
    fieldsToNormalize.forEach(field => {
      // Проверяем, является ли поле camelCase
      if (field !== field.toLowerCase() && /[A-Z]/.test(field)) {
        const camelValue = result[field];
        const snakeKey = field.replace(/([A-Z])/g, '_$1').toLowerCase();
        
        // Если есть значение в camelCase и нет snake_case версии, добавляем snake_case
        if (camelValue !== undefined && !Object.prototype.hasOwnProperty.call(result, snakeKey)) {
          result[snakeKey] = camelValue;
        }
        // Если есть и camelCase и snake_case, приоритет у snake_case (удаляем camelCase)
        else if (camelValue !== undefined && Object.prototype.hasOwnProperty.call(result, snakeKey)) {
          delete result[field];
        }
      }
      // Если поле уже в snake_case, оставляем как есть
      else if (field.includes('_')) {
        // Поле уже в правильном формате
      }
    });
    return result;
  }

  // Нормализуем все поля: конвертируем camelCase в snake_case
  // Но сохраняем существующие snake_case поля
  const normalized = {};
  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      // Если поле уже в snake_case, оставляем как есть
      if (key.includes('_')) {
        normalized[key] = result[key];
      } else {
        // Конвертируем camelCase в snake_case
        const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        // Если snake_case версия уже есть, используем её, иначе добавляем новую
        if (!Object.prototype.hasOwnProperty.call(normalized, snakeKey)) {
          normalized[snakeKey] = result[key];
        }
      }
    }
  }
  return normalized;
}
